import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Download, Brain, TrendingUp, TrendingDown, DollarSign,
  ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  BarChart3, PieChart, Eye, Activity, FileText, Filter,
  ArrowRightLeft, Plus, Minus, X as XIcon, Target, Clock
} from 'lucide-react';
import './ThirteenFPage.css';

// =============================================================================
// MOCK DATA — Individual 13F position changes from quarterly filings
// Each row = one position change from one filer's 13F
// =============================================================================

const mockFilings = [
  { id: 1, institution: 'Citadel Advisors LLC', cik: '0001423053', type: 'Hedge Fund', ticker: 'NVDA', company: 'NVIDIA Corp', action: 'New Position', shares: 3200000, value: 2240000000, prevShares: 0, priceAtFiling: 700.00, quarter: 'Q4 2025', filedDate: '2026-02-05', returnSince: 4.8 },
  { id: 2, institution: 'Berkshire Hathaway', cik: '0001067983', type: 'Holding Company', ticker: 'OXY', company: 'Occidental Petroleum', action: 'Increased', shares: 248000000, value: 14880000000, prevShares: 239600000, priceAtFiling: 60.00, quarter: 'Q4 2025', filedDate: '2026-02-03', returnSince: -1.2 },
  { id: 3, institution: 'Renaissance Technologies', cik: '0001037389', type: 'Hedge Fund', ticker: 'TSLA', company: 'Tesla Inc', action: 'Sold All', shares: 0, value: 0, prevShares: 2100000, priceAtFiling: 394.71, quarter: 'Q4 2025', filedDate: '2026-01-29', returnSince: 0 },
  { id: 4, institution: 'Vanguard Group', cik: '0000102909', type: 'Investment Advisor', ticker: 'AAPL', company: 'Apple Inc', action: 'Increased', shares: 1290000000, value: 305940000000, prevShares: 1265000000, priceAtFiling: 237.10, quarter: 'Q4 2025', filedDate: '2026-02-05', returnSince: 2.1 },
  { id: 5, institution: 'Bridgewater Associates', cik: '0001350694', type: 'Hedge Fund', ticker: 'GLD', company: 'SPDR Gold Trust', action: 'Increased', shares: 6200000, value: 1541200000, prevShares: 4700000, priceAtFiling: 248.58, quarter: 'Q4 2025', filedDate: '2026-01-30', returnSince: 3.4 },
  { id: 6, institution: 'Point72 Asset Mgmt', cik: '0001603466', type: 'Hedge Fund', ticker: 'PLTR', company: 'Palantir Technologies', action: 'New Position', shares: 4500000, value: 371700000, prevShares: 0, priceAtFiling: 82.60, quarter: 'Q4 2025', filedDate: '2026-01-20', returnSince: 8.2 },
  { id: 7, institution: 'BlackRock Inc', cik: '0001364742', type: 'Investment Advisor', ticker: 'MSFT', company: 'Microsoft Corp', action: 'Increased', shares: 342000000, value: 145113000000, prevShares: 335000000, priceAtFiling: 424.28, quarter: 'Q4 2025', filedDate: '2026-02-04', returnSince: 1.5 },
  { id: 8, institution: 'Millennium Management', cik: '0001273087', type: 'Hedge Fund', ticker: 'META', company: 'Meta Platforms', action: 'Decreased', shares: 1800000, value: 1146600000, prevShares: 3200000, priceAtFiling: 637.00, quarter: 'Q4 2025', filedDate: '2026-01-21', returnSince: -2.4 },
  { id: 9, institution: 'T. Rowe Price', cik: '0001003078', type: 'Investment Advisor', ticker: 'AMZN', company: 'Amazon.com Inc', action: 'Decreased', shares: 28000000, value: 6468000000, prevShares: 34000000, priceAtFiling: 231.00, quarter: 'Q4 2025', filedDate: '2026-01-25', returnSince: 3.8 },
  { id: 10, institution: 'Two Sigma Investments', cik: '0001179392', type: 'Hedge Fund', ticker: 'SPY', company: 'SPDR S&P 500 ETF', action: 'New Position', shares: 890000, value: 530740000, prevShares: 0, priceAtFiling: 596.34, quarter: 'Q4 2025', filedDate: '2026-01-24', returnSince: 1.1 },
  { id: 11, institution: 'Citadel Advisors LLC', cik: '0001423053', type: 'Hedge Fund', ticker: 'GOOGL', company: 'Alphabet Inc', action: 'Increased', shares: 5400000, value: 1074600000, prevShares: 3800000, priceAtFiling: 199.00, quarter: 'Q4 2025', filedDate: '2026-02-05', returnSince: 2.9 },
  { id: 12, institution: 'Bridgewater Associates', cik: '0001350694', type: 'Hedge Fund', ticker: 'EEM', company: 'iShares MSCI Emerging', action: 'Sold All', shares: 0, value: 0, prevShares: 3400000, priceAtFiling: 42.80, quarter: 'Q4 2025', filedDate: '2026-01-30', returnSince: 0 },
  { id: 13, institution: 'JPMorgan Chase', cik: '0000019617', type: 'Bank', ticker: 'AAPL', company: 'Apple Inc', action: 'Increased', shares: 45200000, value: 10718920000, prevShares: 42100000, priceAtFiling: 237.10, quarter: 'Q4 2025', filedDate: '2026-02-01', returnSince: 2.1 },
  { id: 14, institution: 'Goldman Sachs', cik: '0000886982', type: 'Bank', ticker: 'JPM', company: 'JPMorgan Chase & Co', action: 'Decreased', shares: 2100000, value: 546000000, prevShares: 2800000, priceAtFiling: 260.00, quarter: 'Q4 2025', filedDate: '2026-01-26', returnSince: 1.8 },
  { id: 15, institution: 'Capital Research', cik: '0000044866', type: 'Investment Advisor', ticker: 'META', company: 'Meta Platforms', action: 'Increased', shares: 12400000, value: 7898800000, prevShares: 9200000, priceAtFiling: 637.00, quarter: 'Q4 2025', filedDate: '2026-01-23', returnSince: -2.4 },
  { id: 16, institution: 'Wellington Mgmt', cik: '0000105897', type: 'Investment Advisor', ticker: 'AVGO', company: 'Broadcom Inc', action: 'New Position', shares: 1800000, value: 414000000, prevShares: 0, priceAtFiling: 230.00, quarter: 'Q4 2025', filedDate: '2026-01-22', returnSince: 6.1 },
  { id: 17, institution: 'FMR LLC (Fidelity)', cik: '0000315066', type: 'Investment Advisor', ticker: 'NVDA', company: 'NVIDIA Corp', action: 'Increased', shares: 98000000, value: 68600000000, prevShares: 92000000, priceAtFiling: 700.00, quarter: 'Q4 2025', filedDate: '2026-02-02', returnSince: 4.8 },
  { id: 18, institution: 'State Street Corp', cik: '0000093751', type: 'Investment Advisor', ticker: 'XLE', company: 'Energy Select SPDR', action: 'Decreased', shares: 4200000, value: 362040000, prevShares: 5800000, priceAtFiling: 86.20, quarter: 'Q4 2025', filedDate: '2026-02-03', returnSince: -3.1 },
  { id: 19, institution: 'Millennium Management', cik: '0001273087', type: 'Hedge Fund', ticker: 'SMCI', company: 'Super Micro Computer', action: 'New Position', shares: 2400000, value: 91200000, prevShares: 0, priceAtFiling: 38.00, quarter: 'Q4 2025', filedDate: '2026-01-21', returnSince: 12.4 },
  { id: 20, institution: 'Geode Capital Mgmt', cik: '0001214717', type: 'Investment Advisor', ticker: 'COST', company: 'Costco Wholesale', action: 'Increased', shares: 3800000, value: 3534000000, prevShares: 3500000, priceAtFiling: 930.00, quarter: 'Q4 2025', filedDate: '2026-01-28', returnSince: 1.9 },
];

// Most bought stocks across all 13F filings this quarter
const mockMostBought = [
  { ticker: 'NVDA', company: 'NVIDIA Corp', buyers: 14, totalShares: 48200000, totalValue: 33740000000 },
  { ticker: 'AAPL', company: 'Apple Inc', buyers: 12, totalShares: 38400000, totalValue: 9106000000 },
  { ticker: 'MSFT', company: 'Microsoft Corp', buyers: 11, totalShares: 22100000, totalValue: 9376000000 },
  { ticker: 'META', company: 'Meta Platforms', buyers: 9, totalShares: 18600000, totalValue: 11846000000 },
  { ticker: 'PLTR', company: 'Palantir', buyers: 8, totalShares: 12800000, totalValue: 1057000000 },
];

// Most sold stocks across all 13F filings this quarter
const mockMostSold = [
  { ticker: 'TSLA', company: 'Tesla Inc', sellers: 11, totalShares: -14200000, totalValue: -5605000000 },
  { ticker: 'XLE', company: 'Energy SPDR', sellers: 8, totalShares: -8900000, totalValue: -767000000 },
  { ticker: 'EEM', company: 'Emerging Markets', sellers: 7, totalShares: -6200000, totalValue: -265000000 },
  { ticker: 'TLT', company: '20+ Year Treasury', sellers: 6, totalShares: -4800000, totalValue: -424000000 },
  { ticker: 'JPM', company: 'JPMorgan Chase', sellers: 5, totalShares: -3100000, totalValue: -806000000 },
];

// Filing deadline tracker
const mockFilingDeadlines = [
  { quarter: 'Q4 2025', deadline: '2026-02-14', totalExpected: 4200, filed: 3180, pctFiled: 75.7 },
  { quarter: 'Q3 2025', deadline: '2025-11-14', totalExpected: 4150, filed: 4150, pctFiled: 100 },
];

// Action breakdown
const mockActionBreakdown = [
  { action: 'New Position', count: 4842, pct: 28.2 },
  { action: 'Increased', count: 6124, pct: 35.7 },
  { action: 'Decreased', count: 3856, pct: 22.5 },
  { action: 'Sold All', count: 2328, pct: 13.6 },
];

// =============================================================================
// HELPERS
// =============================================================================

const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

const formatNumber = (num) => num?.toLocaleString() ?? '—';

const getActionColor = (action) => {
  switch (action) {
    case 'New Position': return 'new';
    case 'Increased': return 'increased';
    case 'Decreased': return 'decreased';
    case 'Sold All': return 'sold';
    default: return '';
  }
};

const getActionIcon = (action) => {
  switch (action) {
    case 'New Position': return Plus;
    case 'Increased': return TrendingUp;
    case 'Decreased': return TrendingDown;
    case 'Sold All': return XIcon;
    default: return Activity;
  }
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function AIInsightCard({ insight }) {
  return (
    <div className="tf-ai-insight">
      <div className="tf-ai-header">
        <div className="tf-ai-badge"><Brain size={14} /> AI Insight</div>
        <span className="tf-ai-updated">Updated 2 hours ago</span>
      </div>
      <p className="tf-ai-text" dangerouslySetInnerHTML={{ __html: insight }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, subType = 'neutral', color }) {
  return (
    <div className="tf-stat-card" style={{ background: color }}>
      <div className="tf-stat-icon"><Icon size={20} /></div>
      <div className="tf-stat-label">{label}</div>
      <div className="tf-stat-value">{value}</div>
      <div className={`tf-stat-sub ${subType}`}>{sub}</div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ThirteenFPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortBy, setSortBy] = useState('filedDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const actionFilters = ['All', 'New Position', 'Increased', 'Decreased', 'Sold All'];
  const typeFilters = ['All', 'Hedge Funds', 'Advisors', 'Banks'];

  const getTypeMatch = (type, filter) => {
    if (filter === 'All') return true;
    if (filter === 'Hedge Funds') return type === 'Hedge Fund';
    if (filter === 'Advisors') return type === 'Investment Advisor';
    if (filter === 'Banks') return type === 'Bank' || type === 'Holding Company';
    return true;
  };

  const filteredFilings = useMemo(() => {
    let data = [...mockFilings];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(f => f.ticker.toLowerCase().includes(q) || f.company.toLowerCase().includes(q) || f.institution.toLowerCase().includes(q));
    }
    if (actionFilter !== 'All') data = data.filter(f => f.action === actionFilter);
    if (typeFilter !== 'All') data = data.filter(f => getTypeMatch(f.type, typeFilter));

    data.sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1;
      if (sortBy === 'institution') return order * a.institution.localeCompare(b.institution);
      if (sortBy === 'ticker') return order * a.ticker.localeCompare(b.ticker);
      if (sortBy === 'value') return order * (a.value - b.value);
      if (sortBy === 'shares') return order * (a.shares - b.shares);
      if (sortBy === 'filedDate') return order * a.filedDate.localeCompare(b.filedDate);
      if (sortBy === 'returnSince') return order * (a.returnSince - b.returnSince);
      return 0;
    });
    return data;
  }, [searchTerm, actionFilter, typeFilter, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredFilings.length / perPage);
  const pagedFilings = filteredFilings.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const shareChange = (curr, prev) => {
    if (prev === 0) return 'New';
    if (curr === 0) return 'Exit';
    const pct = ((curr - prev) / prev * 100).toFixed(1);
    return `${pct > 0 ? '+' : ''}${pct}%`;
  };

  const stats = useMemo(() => {
    const totalValue = mockFilings.reduce((s, f) => s + f.value, 0);
    const newPositions = mockFilings.filter(f => f.action === 'New Position').length;
    const soldAll = mockFilings.filter(f => f.action === 'Sold All').length;
    const filedCount = mockFilings.length;
    return { totalValue, newPositions, soldAll, filedCount };
  }, []);

  const currentDeadline = mockFilingDeadlines[0];

  const aiInsight = `<span class="highlight">Q4 2025 13F filing season is 75.7% complete</span> with the Feb 14 deadline approaching. Key moves: <span class="highlight">Citadel</span> opened a massive $2.24B NVDA position, <span class="highlight">Berkshire</span> continues loading OXY (+8.4M shares), and <span class="highlight">Renaissance</span> fully exited Tesla. Across all filers, NVDA is the most bought stock (14 institutions adding) while TSLA leads sells (11 institutions reducing). Hedge fund new position activity is running 22% above last quarter.`;

  return (
    <div className="thirteen-f-page">
      {/* Header */}
      <div className="tf-page-header">
        <div className="tf-header-content">
          <div className="tf-header-title">
            <div className="tf-header-icon"><FileText size={22} color="#fff" /></div>
            <div>
              <h1>13F Filings</h1>
              <p>Quarterly position changes from institutional investors (SEC Form 13F-HR)</p>
            </div>
          </div>
          <div className="tf-header-actions">
            <button className="tf-header-btn"><Download size={14} /> Export</button>
            <button className="tf-header-btn primary"><Bell size={14} /> Set Alerts</button>
          </div>
        </div>
      </div>

      {/* AI Insight */}
      <AIInsightCard insight={aiInsight} />

      {/* Stats Row */}
      <div className="tf-stats-row">
        <StatCard icon={FileText} label="Position Changes" value={stats.filedCount.toString()} sub={`${formatCurrency(stats.totalValue)} total value`} subType="neutral" color="linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))" />
        <StatCard icon={Plus} label="New Positions" value={stats.newPositions.toString()} sub="First-time buys this quarter" subType="positive" color="linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))" />
        <StatCard icon={XIcon} label="Full Exits" value={stats.soldAll.toString()} sub="Positions completely closed" subType="negative" color="linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))" />
        <StatCard icon={Clock} label="Filing Deadline" value="Feb 14" sub={`${currentDeadline.pctFiled}% filed (${formatNumber(currentDeadline.filed)} of ${formatNumber(currentDeadline.totalExpected)})`} subType="neutral" color="linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))" />
      </div>

      {/* Content Grid */}
      <div className="tf-content-grid">
        {/* Main Table */}
        <div className="tf-table-panel">
          <div className="tf-panel-header">
            <h2><FileText size={16} /> Recent 13F Position Changes <span className="tf-record-count">{filteredFilings.length}</span></h2>
          </div>

          {/* Toolbar */}
          <div className="tf-toolbar">
            <div className="tf-search-box">
              <Search size={14} />
              <input type="text" placeholder="Search stock, institution..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="tf-filter-group">
              <div className="tf-filter-toggles">
                {actionFilters.map(f => (
                  <button key={f} className={`tf-filter-btn ${f !== 'All' ? getActionColor(f) : ''} ${actionFilter === f ? 'active' : ''}`} onClick={() => { setActionFilter(f); setCurrentPage(1); }}>{f}</button>
                ))}
              </div>
              <div className="tf-filter-toggles secondary">
                {typeFilters.map(f => (
                  <button key={f} className={`tf-filter-btn ${typeFilter === f ? 'active' : ''}`} onClick={() => { setTypeFilter(f); setCurrentPage(1); }}>{f}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Table */}
          {filteredFilings.length === 0 ? (
            <div className="tf-empty-state"><Search size={40} /><p>No filings match your filters</p></div>
          ) : (
            <div className="tf-table-wrap">
              <table className="tf-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('institution')}>Institution <SortIcon field="institution" /></th>
                    <th onClick={() => handleSort('ticker')}>Stock <SortIcon field="ticker" /></th>
                    <th>Action</th>
                    <th onClick={() => handleSort('shares')}>Shares <SortIcon field="shares" /></th>
                    <th onClick={() => handleSort('value')}>Value <SortIcon field="value" /></th>
                    <th>Change</th>
                    <th onClick={() => handleSort('filedDate')}>Filed <SortIcon field="filedDate" /></th>
                    <th onClick={() => handleSort('returnSince')}>Return <SortIcon field="returnSince" /></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFilings.map(filing => {
                    const ActionIcon = getActionIcon(filing.action);
                    return (
                      <tr key={filing.id} onClick={() => navigate(`/institution/${encodeURIComponent(filing.institution)}`)}>
                        <td>
                          <span className="tf-inst-name">{filing.institution}</span>
                          <span className="tf-inst-type">{filing.type}</span>
                        </td>
                        <td>
                          <span className="tf-stock-ticker">{filing.ticker}</span>
                          <span className="tf-stock-name">{filing.company}</span>
                        </td>
                        <td>
                          <span className={`tf-action-badge ${getActionColor(filing.action)}`}>
                            <ActionIcon size={11} /> {filing.action}
                          </span>
                        </td>
                        <td>{filing.shares > 0 ? formatNumber(filing.shares) : '—'}</td>
                        <td><span className="tf-value">{filing.value > 0 ? formatCurrency(filing.value) : '—'}</span></td>
                        <td>
                          <span className={`tf-change ${filing.action === 'New Position' || filing.action === 'Increased' ? 'positive' : 'negative'}`}>
                            {shareChange(filing.shares, filing.prevShares)}
                          </span>
                        </td>
                        <td>{new Date(filing.filedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                        <td>
                          {filing.returnSince !== 0 ? (
                            <span className={`tf-return ${filing.returnSince >= 0 ? 'positive' : 'negative'}`}>
                              {filing.returnSince >= 0 ? '+' : ''}{filing.returnSince}%
                            </span>
                          ) : '—'}
                        </td>
                        <td>
                          <button className="tf-chart-btn" onClick={(e) => { e.stopPropagation(); navigate(`/chart/${filing.ticker}`); }} title={`Chart ${filing.ticker}`}>
                            <BarChart3 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="tf-pagination">
              <span className="tf-pagination-info">Showing {pagedFilings.length} of {filteredFilings.length} filings</span>
              <div className="tf-pagination-btns">
                <button className="tf-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={14} /></button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i + 1} className={`tf-page-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                ))}
                <button className="tf-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="tf-sidebar">
          {/* Filing Deadline Tracker */}
          <div className="tf-sidebar-card">
            <div className="tf-card-header">
              <h3><Clock size={16} color="#f59e0b" /> Filing Season Tracker</h3>
            </div>
            <div className="tf-deadline-card">
              <div className="tf-deadline-info">
                <span className="tf-deadline-quarter">{currentDeadline.quarter}</span>
                <span className="tf-deadline-date">Due: {new Date(currentDeadline.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="tf-deadline-bar-wrap">
                <div className="tf-deadline-bar" style={{ width: `${currentDeadline.pctFiled}%` }} />
              </div>
              <div className="tf-deadline-stats">
                <span>{formatNumber(currentDeadline.filed)} filed</span>
                <span>{formatNumber(currentDeadline.totalExpected - currentDeadline.filed)} remaining</span>
              </div>
            </div>
          </div>

          {/* Action Breakdown */}
          <div className="tf-sidebar-card">
            <div className="tf-card-header">
              <h3><PieChart size={16} color="#667eea" /> Action Breakdown</h3>
            </div>
            <div className="tf-action-list">
              {mockActionBreakdown.map((a, i) => (
                <div className="tf-action-row" key={i}>
                  <span className={`tf-action-dot ${getActionColor(a.action)}`} />
                  <span className="tf-action-label">{a.action}</span>
                  <div className="tf-action-bar-wrap">
                    <div className={`tf-action-bar ${getActionColor(a.action)}`} style={{ width: `${a.pct}%` }} />
                  </div>
                  <span className="tf-action-count">{formatNumber(a.count)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Most Bought */}
          <div className="tf-sidebar-card">
            <div className="tf-card-header">
              <h3><TrendingUp size={16} color="#10b981" /> Most Bought</h3>
            </div>
            <div className="tf-stock-list">
              {mockMostBought.map((s, i) => (
                <div className="tf-stock-item" key={i} onClick={() => navigate(`/stock/${s.ticker}`)}>
                  <span className={`tf-stock-rank ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : ''}`}>{i + 1}</span>
                  <div className="tf-stock-info">
                    <span className="tf-stock-tk">{s.ticker}</span>
                    <span className="tf-stock-co">{s.company}</span>
                  </div>
                  <div className="tf-stock-data">
                    <span className="tf-stock-buyers positive">{s.buyers} buyers</span>
                    <span className="tf-stock-val">{formatCurrency(s.totalValue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Most Sold */}
          <div className="tf-sidebar-card">
            <div className="tf-card-header">
              <h3><TrendingDown size={16} color="#ef4444" /> Most Sold</h3>
            </div>
            <div className="tf-stock-list">
              {mockMostSold.map((s, i) => (
                <div className="tf-stock-item" key={i} onClick={() => navigate(`/stock/${s.ticker}`)}>
                  <span className="tf-stock-rank">{i + 1}</span>
                  <div className="tf-stock-info">
                    <span className="tf-stock-tk">{s.ticker}</span>
                    <span className="tf-stock-co">{s.company}</span>
                  </div>
                  <div className="tf-stock-data">
                    <span className="tf-stock-buyers negative">{s.sellers} sellers</span>
                    <span className="tf-stock-val">{formatCurrency(Math.abs(s.totalValue))}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}